import React, { useRef, useState } from "react";
import axios from "axios";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CircularProgress from "@mui/material/CircularProgress";
import { API_setup_assistant_upload_roster } from "../../../constants";
import { getDefaultAxiosConfig } from "../../../utilities/requests";

// The backend names the endpoint by key so the chat never carries a raw URL.
const URL_BY_KEY = {
  API_setup_assistant_upload_roster,
};

const ACCEPT_FALLBACK = [".csv", ".xlsx", ".pdf"];
// Mirrors Django's EmailValidator closely enough that a row the form accepts
// is a row the import accepts: a dot-atom local part and a hostname whose
// labels are alphanumeric (hyphens inside only) with a letter-only TLD.
const EMAIL_RE = /^[-!#$%&'*+/=?^_`{}|~0-9A-Za-z]+(?:\.[-!#$%&'*+/=?^_`{}|~0-9A-Za-z]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/;
// The email becomes the account username, which is capped at 150.
const EMAIL_MAX_LEN = 150;
const MAX_FORM_ROWS = 100;
const emptyPerson = () => ({ first_name: "", last_name: "", email: "", phone: "", role: "", permission_type: "company_employee" });
const PERMISSION_OPTIONS = [
  ["company_employee", "Employee"],
  ["company_manager", "Manager"],
];

/**
 * Setup Assistant roster upload card. The file is posted straight to the
 * roster endpoint and parsed there; only the parsed rows come back, and
 * they are handed to the panel through onUploaded. Nothing about the file
 * reaches the chat transcript or the assistant.
 */
export default function RosterUploadCard({
  request,
  companyId,
  status = "pending",
  onStatusChange,
  onUploaded,
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [outcome, setOutcome] = useState(null);
  // "file" (upload) is the default and first tab: most teams arrive with a
  // spreadsheet; "form" (enter people) is for a handful of names.
  const [mode, setMode] = useState("file");
  const [people, setPeople] = useState(() => [emptyPerson(), emptyPerson(), emptyPerson()]);
  const [formError, setFormError] = useState(null);
  const roles = Array.isArray(request?.roles) ? request.roles : [];

  const title = request?.title || "Upload your team roster";
  const accept = Array.isArray(request?.accept) && request.accept.length > 0 ? request.accept : ACCEPT_FALLBACK;
  const columns = Array.isArray(request?.columns) ? request.columns : [];
  const submitUrl = URL_BY_KEY[request?.submit?.url_key];
  const isUploading = status === "uploading";
  const inputId = `roster-upload-${request?.request_id || "request"}`;

  const setStatus = (nextStatus, uploadOutcome) =>
    onStatusChange?.(request.request_id, nextStatus, uploadOutcome);

  const handleDismiss = () => setStatus("dismissed");

  const updatePerson = (index, field, value) =>
    setPeople((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  const addPerson = () => setPeople((prev) => (prev.length < MAX_FORM_ROWS ? [...prev, emptyPerson()] : prev));
  const removePerson = (index) => setPeople((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  // Rows a person actually started typing; blank rows are ignored.
  const filledPeople = people.filter((p) =>
    Object.entries(p).some(([field, v]) => field !== "permission_type" && String(v || "").trim())
  );

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (isUploading) return;
    const rows = [];
    const seen = new Set();
    for (const [index, person] of filledPeople.entries()) {
      const first = person.first_name.trim();
      const last = person.last_name.trim();
      const email = person.email.trim().toLowerCase();
      if (!first || !last) {
        setFormError(`Row ${index + 1} needs a first and last name.`);
        return;
      }
      if (!EMAIL_RE.test(email) || email.length > EMAIL_MAX_LEN) {
        setFormError(`${first || last} needs a valid email address (up to ${EMAIL_MAX_LEN} characters).`);
        return;
      }
      if (seen.has(email)) {
        setFormError(`${email} is listed twice.`);
        return;
      }
      seen.add(email);
      rows.push({
        row_key: index + 1,
        first_name: first.slice(0, 100),
        last_name: last.slice(0, 100),
        email,
        phone: person.phone.trim().slice(0, 25),
        role: person.role.trim().slice(0, 50),
        permission_type: PERMISSION_OPTIONS.some(([v]) => v === person.permission_type) ? person.permission_type : "company_employee",
        blocked: false,
      });
    }
    if (rows.length === 0) {
      setFormError("Add at least one person.");
      return;
    }
    setFormError(null);
    const counts = { total: rows.length, importable: rows.length, blocked: 0 };
    const result = { file_name: "Entered in chat", count: rows.length };
    setOutcome({ ...result, counts, issues: [] });
    setStatus("uploaded", result);
    onUploaded?.({ file_name: result.file_name, rows, counts, issues: [] });
  };

  const handleFile = async (event) => {
    const file = event.target?.files?.[0];
    // Reset so picking the same file again re-fires onChange.
    if (event.target) event.target.value = "";
    if (!file || isUploading) return;
    if (!submitUrl) {
      setError("Roster uploads are not available from chat yet. Add people from the Team page instead.");
      return;
    }
    const lower = (file.name || "").toLowerCase();
    if (!accept.some((ext) => lower.endsWith(ext))) {
      setError(`Choose a ${accept.join(" or ")} file.`);
      return;
    }
    setError(null);
    setFileName(file.name);
    setStatus("uploading");
    const formData = new FormData();
    formData.append("company_id", companyId);
    formData.append("file", file, file.name);
    try {
      // Let axios set the multipart boundary.
      const response = await axios.post(submitUrl, formData, {
        ...(getDefaultAxiosConfig() || {}),
        timeout: 60000,
      });
      const payload = response?.data?.redux?.payload;
      const rows = Array.isArray(payload?.rows) ? payload.rows : null;
      if (!rows || rows.length === 0) {
        setError("No people were found in that file. Check the columns and try again.");
        setStatus("pending");
        return;
      }
      const counts = payload.counts || { total: rows.length, importable: rows.length, blocked: 0 };
      const result = { file_name: payload.file_name || file.name, count: counts.total };
      setOutcome({ ...result, counts, issues: Array.isArray(payload.issues) ? payload.issues : [] });
      setStatus("uploaded", result);
      onUploaded?.({ file_name: result.file_name, rows, counts, issues: payload.issues || [] });
    } catch (err) {
      const serverMessage = err?.response?.data?.system?.message;
      if (err?.response?.status === 429) {
        setError(serverMessage || "Too many attempts. Please wait a moment and try again.");
      } else {
        setError(serverMessage || "Couldn't read that file. Please try again.");
      }
      setStatus("pending");
    }
  };

  if (status === "uploaded") {
    const counts = outcome?.counts;
    const attention = counts?.blocked ? `, ${counts.blocked} need attention` : "";
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-sm my-2 bg-green-50 border border-green-200 text-green-700">
        <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">
            {outcome?.file_name || fileName || "Roster"}: {counts?.total ?? outcome?.count ?? 0} people found{attention}
          </div>
          {outcome?.issues?.length > 0 && (
            <div className="text-xs opacity-80">{outcome.issues.map((i) => i.message).join(" ")}</div>
          )}
        </div>
      </div>
    );
  }

  if (status === "dismissed") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Skipped for now</span>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <GroupAddIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">{title}</span>
      </div>

      <div className="flex gap-1 px-3 pt-2" role="tablist" aria-label="How to add your team">
        {[["file", "Upload a file"], ["form", "Enter people"]].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={mode === key}
            onClick={() => setMode(key)}
            className={`px-3 py-1 text-xs rounded-full border ${
              mode === key
                ? "bg-violet-700 text-white border-violet-700"
                : "bg-white text-violet-700 border-violet-300 hover:bg-violet-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "form" && (
        <form className="px-3 py-3 space-y-2" onSubmit={handleFormSubmit}>
          <div className="text-xs text-gray-500">Name and email are required; phone and role are optional. Access is Employee unless you pick Manager.</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pr-1 py-1">First name</th>
                  <th className="pr-1 py-1">Last name</th>
                  <th className="pr-1 py-1">Email</th>
                  <th className="pr-1 py-1">Phone</th>
                  <th className="pr-1 py-1">Role</th>
                  <th className="pr-1 py-1">Access</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody>
                {people.map((person, index) => (
                  <tr key={index}>
                    {[["first_name", "First name", "text", 100], ["last_name", "Last name", "text", 100], ["email", "Email", "email", 150], ["phone", "Phone", "tel", 25]].map(([field, label, type, max]) => (
                      <td key={field} className="pr-1 py-0.5">
                        <input
                          type={type}
                          aria-label={`${label} ${index + 1}`}
                          maxLength={max}
                          value={person[field]}
                          disabled={isUploading}
                          onChange={(e) => updatePerson(index, field, e.target.value)}
                          className="w-full min-w-[6rem] text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
                        />
                      </td>
                    ))}
                    <td className="pr-1 py-0.5">
                      {roles.length > 0 ? (
                        <select
                          aria-label={`Role ${index + 1}`}
                          value={person.role}
                          disabled={isUploading}
                          onChange={(e) => updatePerson(index, "role", e.target.value)}
                          className="w-full min-w-[6rem] text-xs border border-gray-300 rounded-md px-1 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
                        >
                          <option value="">No role</option>
                          {roles.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          aria-label={`Role ${index + 1}`}
                          maxLength={50}
                          value={person.role}
                          disabled={isUploading}
                          onChange={(e) => updatePerson(index, "role", e.target.value)}
                          className="w-full min-w-[6rem] text-xs border border-gray-300 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
                        />
                      )}
                    </td>
                    <td className="pr-1 py-0.5">
                      <select
                        aria-label={`Access ${index + 1}`}
                        value={person.permission_type}
                        disabled={isUploading}
                        onChange={(e) => updatePerson(index, "permission_type", e.target.value)}
                        className="w-full min-w-[6rem] text-xs border border-gray-300 rounded-md px-1 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
                      >
                        {PERMISSION_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-0.5">
                      <button
                        type="button"
                        aria-label={`Remove row ${index + 1}`}
                        onClick={() => removePerson(index)}
                        disabled={isUploading || people.length === 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-30 px-1"
                      >
                        x
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={addPerson}
              disabled={isUploading || people.length >= MAX_FORM_ROWS}
              className="text-xs text-violet-700 hover:underline disabled:opacity-50"
            >
              + Add another person
            </button>
            <button
              type="submit"
              disabled={isUploading || filledPeople.length === 0}
              className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50"
            >
              Continue with {filledPeople.length} {filledPeople.length === 1 ? "person" : "people"}
            </button>
          </div>
          {formError && (
            <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {formError}
            </div>
          )}
        </form>
      )}

      {mode === "file" && (
      <div className="px-3 py-3 space-y-3">
        {columns.length > 0 && (
          <div className="text-sm text-gray-600">
            <div className="text-xs font-medium text-gray-500 mb-1">Columns</div>
            <div>{columns.join(", ")}</div>
          </div>
        )}
        {request?.template_note && (
          <div className="text-xs text-gray-500">{request.template_note}</div>
        )}

        <label
          htmlFor={inputId}
          className={`inline-flex items-center gap-1.5 text-sm rounded-md px-3 py-1.5 bg-violet-700 text-white hover:bg-violet-800 cursor-pointer ${
            isUploading ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {isUploading ? (
            <CircularProgress size={14} sx={{ color: "white" }} />
          ) : (
            <UploadFileIcon style={{ fontSize: "18px" }} />
          )}
          <span>{isUploading ? `Reading ${fileName || "file"}...` : "Choose file"}</span>
          <input
            id={inputId}
            ref={inputRef}
            data-testid="roster-upload-input"
            type="file"
            accept={accept.join(",")}
            className="hidden"
            onChange={handleFile}
            disabled={isUploading}
          />
        </label>

        {error && (
          <div role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}
      </div>
      )}

      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          type="button"
          onClick={handleDismiss}
          disabled={isUploading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
